// ============================================================
// FILE: ForgotPassword.js
// LOCATION: C:\gardenmarket\web\src\pages\public\ForgotPassword.js
// ============================================================
// PURPOSE:
//   "Forgot my password" — collects an email and asks the API to
//   send a reset link.
//
// NOTE ON THE SUCCESS MESSAGE:
//   The API deliberately returns the same response whether or not
//   the email is registered, so this page must NOT say "no account
//   found" or similar. Doing so would let anyone check which email
//   addresses have accounts. The wording below ("if an account
//   exists...") is intentional — please leave it.
// ============================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';

export default function ForgotPassword() {
    const [email,   setEmail]   = useState('');
    const [sent,    setSent]    = useState(false);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');

    const handleSubmit = async () => {
        if (!email.trim()) {
            setError('Please enter your email address.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await client.post('/users/forgot-password', { email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.icon}>📧</div>
                    <h1 style={styles.title}>Check your email</h1>
                    <p style={styles.text}>
                        If an account exists for <strong>{email}</strong>, we've sent
                        a link to reset your password.
                    </p>
                    <p style={styles.hint}>
                        The link expires in 1 hour. If it doesn't arrive within a few
                        minutes, check your spam folder.
                    </p>
                    <Link to="/login" style={styles.btnSecondary}>Back to login</Link>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h1 style={styles.title}>Forgot your password?</h1>
                <p style={styles.text}>
                    Enter the email address you signed up with and we'll send you a
                    link to set a new password.
                </p>

                {error && <div style={styles.error}>{error}</div>}

                <label style={styles.label}>Email address</label>
                <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    style={styles.input}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                />

                <button
                    onClick={handleSubmit}
                    style={styles.btn}
                    className="gh-touch-target"
                    disabled={loading}
                >
                    {loading ? 'Sending...' : 'Send reset link'}
                </button>

                <p style={styles.footer}>
                    Remembered it? <Link to="/login" style={styles.link}>Back to login</Link>
                </p>
            </div>
        </div>
    );
}

const styles = {
    page:         { maxWidth:'440px', margin:'0 auto', padding:'48px 24px' },
    card:         { background:'white', borderRadius:'12px', padding:'32px', boxShadow:'0 2px 12px rgba(0,0,0,0.08)' },
    icon:         { fontSize:'2.5rem', textAlign:'center', marginBottom:'8px' },
    title:        { color:'#2d6a4f', fontSize:'1.5rem', marginTop:0, marginBottom:'12px', textAlign:'center' },
    text:         { color:'#555', fontSize:'0.92rem', lineHeight:1.6, marginBottom:'20px', textAlign:'center' },
    hint:         { color:'#888', fontSize:'0.84rem', lineHeight:1.6, marginBottom:'24px', textAlign:'center' },
    label:        { display:'block', fontWeight:600, marginBottom:'6px', color:'#333', fontSize:'0.88rem' },
    // 16px font size prevents iOS Safari from zooming on focus
    input:        { width:'100%', padding:'11px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'16px', boxSizing:'border-box', marginBottom:'18px' },
    btn:          { width:'100%', padding:'13px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer' },
    btnSecondary: { display:'block', textAlign:'center', padding:'13px', backgroundColor:'#f5f5f5', color:'#2d6a4f', borderRadius:'8px', fontSize:'0.95rem', fontWeight:600, textDecoration:'none' },
    error:        { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px', padding:'10px 14px', color:'#c0392b', marginBottom:'16px', fontSize:'0.88rem' },
    footer:       { textAlign:'center', marginTop:'20px', marginBottom:0, fontSize:'0.88rem', color:'#666' },
    link:         { color:'#2d6a4f', fontWeight:600, textDecoration:'none' },
};
